import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import useBoolean from "./useBoolean";
import { LoadingButton } from "@mui/lab";

type ConfirmOptions = {
  title?: string;
  content?: string;
  okText?: string;
  cancelText?: string;
  onOk?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
};

export function useConfirmDialog() {
  const [visible, setVisible] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({});
  const [cancelLoading, { set: setCancelLoading }] = useBoolean();
  const [confirmLoading, { set: setConfirmLoading }] = useBoolean();

  const confirm = (opts: ConfirmOptions) => {
    setOptions(opts);
    setVisible(true);
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      await options.onCancel?.();
      setCancelLoading(false);
      setVisible(false);
    } catch (err) {
      setCancelLoading(false);
      setVisible(false);
    }
  };

  const handleConfirm = async () => {
    setConfirmLoading(true);
    try {
      await options.onOk?.();
      setConfirmLoading(false);
      setVisible(false);
    } catch (err) {
      setConfirmLoading(false);
      setVisible(false);
    }
  };

  const ConfirmDialog = (
    <Dialog open={visible} onClose={() => setVisible(false)}>
      <DialogTitle sx={{ fontSize: "1.2rem" }}>{options.title || "Confirm"}</DialogTitle>
      <DialogContent>{options.content}</DialogContent>
      <DialogActions>
        <LoadingButton
          onClick={() => handleCancel()}
          loading={cancelLoading}
          variant="outlined"
          sx={{ borderRadius: "4px" }}
        >
          {options.cancelText || "Cancel"}
        </LoadingButton>
        <LoadingButton
          variant="contained"
          color="primary"
          sx={{ borderRadius: "4px" }}
          onClick={() => handleConfirm()}
          loading={confirmLoading}
        >
          {options.okText || "OK"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );

  return { confirm, ConfirmDialog };
}

export default useConfirmDialog;
